import { PostImage } from "./PostImage";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "urql";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Empty,
  Flex,
  Input,
  Row,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  SearchOutlined,
  ArrowRightOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import {
  FeaturedProductsDocument,
  ProductsDocument,
  ProductDocument,
  PostsDocument,
  ProductThumbnail,
  ProductImage,
  PageHeader,
} from "visumo-data-agent-web-shared";
import { HelpIcon } from "../help/HelpIcon";

function SelectedProduct({ id }: { id: string }) {
  const navigate = useNavigate();
  const [result, reload] = useQuery({
    query: ProductDocument,
    variables: { id },
  });
  if (result.error)
    return (
      <Alert
        type="error"
        title="商品を読み込めませんでした"
        action={
          <Button onClick={() => reload({ requestPolicy: "network-only" })}>
            再読み込み
          </Button>
        }
      />
    );
  if (!result.data) return <Skeleton active />;
  const product = result.data.product;
  if (!product) return <Empty description="この商品を閲覧できません" />;
  return (
    <div className="vd-stack">
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Card styles={{ body: { padding: 12 } }}>
            <ProductImage
              key={product.id}
              imageUrl={product.imageUrl}
              name={product.name}
            />
          </Card>
        </Col>
        <Col xs={24} md={16}>
          <Card
            title="商品説明"
            extra={<Tag color="#7CC7B7">商品マスター</Tag>}
            style={{ height: "100%" }}
          >
            <Typography.Title level={4} style={{ marginTop: 0 }}>
              {product.name}
            </Typography.Title>
            <Space wrap>
              <Tag>{product.brand}</Tag>
              {product.category && <Tag>{product.category}</Tag>}
              {product.price !== null && (
                <Typography.Text strong>
                  {new Intl.NumberFormat("ja-JP", {
                    style: "currency",
                    currency: product.currency ?? "JPY",
                  }).format(product.price)}
                </Typography.Text>
              )}
            </Space>
            <Typography.Paragraph style={{ marginTop: 16, lineHeight: 1.9 }}>
              {product.description ??
                "この商品の説明はまだ作成されていません。"}
            </Typography.Paragraph>
            {product.descriptionSources.length > 0 && (
              <Collapse
                ghost
                size="small"
                items={[
                  {
                    key: "sources",
                    label: "説明の出典",
                    children: (
                      <Typography.Text type="secondary">
                        {product.descriptionSources.join(" ・ ")}
                      </Typography.Text>
                    ),
                  },
                ]}
              />
            )}
            <Flex gap={8} wrap style={{ marginTop: 16 }}>
              <Button
                icon={<ArrowRightOutlined aria-hidden />}
                onClick={() => navigate(`/products/${encodeURIComponent(id)}`)}
              >
                商品詳細を開く
              </Button>
              {product.page && (
                <Button
                  type="primary"
                  icon={<FileTextOutlined aria-hidden />}
                  onClick={() =>
                    navigate(`/products/${encodeURIComponent(id)}#product-page`)
                  }
                >
                  生成ページを見る
                </Button>
              )}
            </Flex>
          </Card>
        </Col>
      </Row>
      {product.aio.tags.length > 0 && (
        <Card title="商品の特徴">
          <Flex wrap gap={8}>
            {product.aio.tags.flatMap((tag) =>
              tag.values.map((value) => (
                <Tag
                  key={`${tag.axis}:${value}`}
                  color={tag.source === "MASTER" ? "green" : "default"}
                >
                  {tag.label}: {value}
                  {tag.source === "INFERRED" ? "（推定）" : ""}
                </Tag>
              )),
            )}
          </Flex>
        </Card>
      )}
      <Card
        title={
          <Space>
            <Badge count={product.aio.faqTotal} color="#7CC7B7" showZero />
            お客様の疑問に答える FAQ
          </Space>
        }
      >
        {product.aio.faqs.length === 0 ? (
          <Empty description="FAQ はまだ作成されていません" />
        ) : (
          <Collapse
            defaultActiveKey={[product.aio.faqs[0]?.id ?? ""]}
            items={product.aio.faqs.map((faq) => ({
              key: faq.id,
              label: faq.question,
              children: (
                <>
                  <Typography.Paragraph style={{ lineHeight: 1.9 }}>
                    {faq.answer}
                  </Typography.Paragraph>
                  <Space wrap>
                    <Tag>{faq.evidence.label}</Tag>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      生成日{" "}
                      {new Date(faq.generatedAt).toLocaleDateString("ja-JP", {
                        timeZone: "Asia/Tokyo",
                      })}
                    </Typography.Text>
                    <Tag>{faq.delivered ? "配信中" : "確認用"}</Tag>
                  </Space>
                </>
              ),
            }))}
          />
        )}
      </Card>
    </div>
  );
}

function RecentPosts() {
  const [result, reload] = useQuery({
    query: PostsDocument,
    variables: { first: 6, hasMedia: true },
  });
  const [opened, setOpened] = useState<string | null>(null);
  const rows = result.data?.posts.edges.map((edge) => edge.node) ?? [];
  return (
    <Card
      title={
        <Space>
          <Badge count={2} color="#65B6D4" />
          投稿を見る
        </Space>
      }
      extra={
        <Link to="/posts">
          すべての投稿 <ArrowRightOutlined />
        </Link>
      }
    >
      <Typography.Paragraph type="secondary">
        この会社の投稿から、画像のあるものを新しい順に表示しています。
      </Typography.Paragraph>
      {result.error ? (
        <Alert
          type="error"
          title="投稿を読み込めませんでした"
          action={
            <Button onClick={() => reload({ requestPolicy: "network-only" })}>
              再読み込み
            </Button>
          }
        />
      ) : !result.data ? (
        <Skeleton active />
      ) : rows.length === 0 ? (
        <Empty
          description={
            result.data.posts.dataGap?.why ?? "投稿はまだ取り込まれていません"
          }
        />
      ) : (
        <Row gutter={[16, 16]}>
          {rows.map((post) => (
            <Col key={post.id} xs={24} sm={12} lg={8}>
              <Card
                hoverable
                onClick={() => setOpened(opened === post.id ? null : post.id)}
                styles={{ body: { padding: 12 } }}
              >
                <PostImage
                  url={post.mediaUrl ?? null}
                  title={post.title ?? "投稿画像"}
                  gallery
                />
                <Button
                  type="link"
                  onClick={(event) => {
                    event.stopPropagation();
                    setOpened(opened === post.id ? null : post.id);
                  }}
                  style={{ padding: 0 }}
                  aria-expanded={opened === post.id}
                >
                  投稿の本文を見る
                </Button>
                <Typography.Paragraph
                  ellipsis={opened === post.id ? false : { rows: 2 }}
                  style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}
                >
                  {post.bodyWithheld
                    ? "本文の閲覧権限がありません"
                    : (post.body ?? post.title ?? "本文なし")}
                </Typography.Paragraph>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Card>
  );
}

export function ProductsAndPostsPage() {
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [featured, reloadFeatured] = useQuery({
    query: FeaturedProductsDocument,
  });
  const [search, reloadSearch] = useQuery({
    query: ProductsDocument,
    variables: {
      first: 12,
      filter: { query },
      order: "NAME_ASC",
      population: "ALL",
    },
    pause:
      query.length === 0 &&
      (featured.fetching ||
        (featured.data?.featuredProducts.length ?? 0) > 0 ||
        !!featured.error),
  });
  const rows = query
    ? (search.data?.products.edges.map((edge) => edge.node) ?? [])
    : featured.data?.featuredProducts.length
      ? featured.data.featuredProducts
      : (search.data?.products.edges.map((edge) => edge.node) ?? []);
  const activeId = rows.some((row) => row.id === selectedId)
    ? selectedId
    : rows[0]?.id;
  const fetching = query
    ? search.fetching
    : featured.fetching || (rows.length === 0 && search.fetching);
  const error = query ? search.error : (featured.error ?? search.error);
  return (
    <div className="vd-stack">
      <PageHeader
        title="商品と投稿"
        description="商品を選んで、商品説明・お客様の疑問への答え・投稿を確認できます。"
        titleExtra={<HelpIcon slug="products/overview" />}
      />
      <Card
        title={
          <Space>
            <Badge count={1} color="#EDAA86" />
            商品を検索して選ぶ
          </Space>
        }
      >
        <Input.Search
          size="large"
          prefix={<SearchOutlined />}
          placeholder="商品名・商品 ID で検索"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onSearch={(value) => {
            setQuery(value.trim());
            setSelectedId(null);
          }}
          allowClear
          style={{ marginBottom: 16 }}
        />
        {!query && rows.length > 0 && (
          <Typography.Paragraph type="secondary">
            {featured.data?.featuredProducts.length
              ? "説明・FAQ を確認できる商品"
              : "商品一覧"}
          </Typography.Paragraph>
        )}
        {error ? (
          <Alert
            type="error"
            title="商品を読み込めませんでした"
            action={
              <Button
                onClick={() => {
                  reloadFeatured({ requestPolicy: "network-only" });
                  reloadSearch({ requestPolicy: "network-only" });
                }}
              >
                再読み込み
              </Button>
            }
          />
        ) : fetching ? (
          <Skeleton active />
        ) : rows.length === 0 ? (
          <Empty description="該当する商品が見つかりません" />
        ) : (
          <Row gutter={[12, 12]}>
            {rows.slice(0, expanded ? rows.length : 6).map((product) => (
              <Col key={product.id} xs={24} sm={12} xl={8}>
                <button
                  className="studio-product-choice"
                  aria-pressed={activeId === product.id}
                  onClick={() => setSelectedId(product.id)}
                >
                  <ProductThumbnail
                    imageUrl={product.imageUrl}
                    name={product.name}
                  />
                  <span style={{ minWidth: 0 }}>
                    <Typography.Text
                      strong
                      style={{ maxWidth: "100%", display: "block" }}
                    >
                      {product.name ?? product.id}
                    </Typography.Text>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {product.category ?? product.brand} ・ {product.id}
                    </Typography.Text>
                  </span>
                </button>
              </Col>
            ))}
          </Row>
        )}
        {rows.length > 6 && (
          <Button
            type="link"
            onClick={() => setExpanded(!expanded)}
            style={{ marginTop: 12 }}
          >
            {expanded
              ? "最初の 6 件に戻す"
              : `残り ${rows.length - 6} 件を見る`}
          </Button>
        )}
      </Card>
      {activeId && <SelectedProduct key={activeId} id={activeId} />}
      <RecentPosts />
    </div>
  );
}
